import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from "@/components/ui/sheet";
import { toast } from "sonner";
import {
  Users,
  Shield,
  Settings,
  Crown,
  Zap,
  Building2,
  Edit,
  Clock,
  ToggleRight,
  DollarSign,
  Loader2,
  Search,
  ShieldCheck,
  Layers,
  Lock,
  Unlock,
  KeyRound,
  BarChart3,
  Brain,
  ArrowRight,
  TrendingUp,
  Eye,
  MousePointer,
  RefreshCw,
  Copy,
  Check,
  Database,
  Trash2,
  AlertTriangle,
} from "lucide-react";

const PLAN_COLORS = { free: "cyan", pro: "amber", enterprise: "violet" };
const PLAN_ICONS = { free: Zap, pro: Crown, enterprise: Building2 };

export default function AdminPage() {
  const { api, user } = useAuth();
  const [tab, setTab] = useState("users");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [gates, setGates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [drawer, setDrawer] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [statsRes, usersRes, plansRes, gatesRes] = await Promise.all([
        api().get("/admin/stats"),
        api().get("/admin/users"),
        api().get("/admin/plans"),
        api().get("/admin/feature-gates"),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setPlans(plansRes.data);
      setGates(gatesRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load admin data");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { loadData(); }, [loadData]);

  const filteredUsers = users.filter(
    (u) =>
      u.email?.toLowerCase().includes(search.toLowerCase()) ||
      u.name?.toLowerCase().includes(search.toLowerCase())
  );

  const handleSetPlan = async (userId, planId) => {
    setSaving(true);
    try {
      await api().put(`/admin/users/${userId}/plan`, { plan_id: planId });
      toast.success("Plan updated");
      loadData();
    } catch (e) { toast.error("Failed to update plan"); }
    finally { setSaving(false); }
  };

  const handleSetBypass = async (userId, bypass, reason) => {
    setSaving(true);
    try {
      await api().put(`/admin/users/${userId}/bypass`, { bypass, bypass_reason: reason });
      toast.success(bypass ? "Bypass enabled" : "Bypass removed");
      loadData();
    } catch (e) { toast.error("Failed to update bypass"); }
    finally { setSaving(false); }
  };

  const handleStartTrial = async (userId, days) => {
    setSaving(true);
    try {
      await api().post(`/admin/users/${userId}/trial`, { trial_days: days });
      toast.success(`${days}-day trial started`);
      loadData();
    } catch (e) { toast.error("Failed to start trial"); }
    finally { setSaving(false); }
  };

  const handleSetRole = async (userId, role) => {
    setSaving(true);
    try {
      await api().put(`/admin/users/${userId}/role`, { role });
      toast.success(`Role set to ${role}`);
      loadData();
    } catch (e) { toast.error("Failed to update role"); }
    finally { setSaving(false); }
  };

  const handleFeatureOverride = async (userId, overrides) => {
    setSaving(true);
    try {
      await api().put(`/admin/users/${userId}/features`, { feature_overrides: overrides });
      toast.success("Feature overrides saved");
      loadData();
    } catch (e) { toast.error("Failed to save overrides"); }
    finally { setSaving(false); }
  };

  const handleUnlockUser = async (userId) => {
    try {
      await api().post(`/admin/users/${userId}/unlock`);
      toast.success("Account unlocked");
      loadData();
    } catch (e) { toast.error("Failed to unlock account"); }
  };

  const handleResetPassword = async (userId, newPassword) => {
    setSaving(true);
    try {
      await api().post(`/admin/users/${userId}/reset-password`, { new_password: newPassword });
      toast.success("Password reset successfully");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to reset password"); }
    finally { setSaving(false); }
  };

  const handleUpdateGate = async (featureKey, updates) => {
    try {
      await api().put(`/admin/feature-gates/${featureKey}`, updates);
      toast.success("Feature gate updated");
      loadData();
    } catch (e) { toast.error("Failed to update gate"); }
  };

  const handleUpdatePlan = async (planId, updates) => {
    try {
      await api().put(`/admin/plans/${planId}`, updates);
      toast.success("Plan updated");
      loadData();
    } catch (e) { toast.error("Failed to update plan"); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-[var(--hig-accent)]" />
      </div>
    );
  }

  if (user?.role !== "admin") {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <Shield className="w-12 h-12 text-zinc-600" />
        <p className="text-[var(--n-text-muted)]">Admin access required</p>
      </div>
    );
  }

  const tabs = [
    { id: "users", label: "Users", icon: Users },
    { id: "plans", label: "Plans", icon: Layers },
    { id: "gates", label: "Feature Gates", icon: ToggleRight },
    { id: "data", label: "Data Management", icon: Database },
    { id: "sso", label: "SSO", icon: Building2 },
    { id: "health", label: "Service Health", icon: Zap },
    { id: "analytics", label: "Analytics", icon: BarChart3 },
  ];

  return (
    <div className="space-y-6 max-w-6xl" data-testid="admin-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Admin Console</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Manage users, plans, and feature access
        </p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-stats">
          {[
            { label: "Total Users", value: stats.total_users, icon: Users, color: "cyan" },
            { label: "Paid Subs", value: stats.paid_subscribers, icon: Crown, color: "amber" },
            { label: "Bypass", value: stats.bypass_users, icon: Shield, color: "violet" },
            { label: "Revenue", value: `$${stats.total_revenue?.toFixed(0) || 0}`, icon: DollarSign, color: "emerald" },
          ].map((s) => (
            <div key={s.label} className="glass-card rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <s.icon className={`w-4 h-4 text-${s.color}-400`} />
                <span className="text-xs text-muted-foreground">{s.label}</span>
              </div>
              <p className="text-2xl font-bold">{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-[var(--n-hover)] rounded-lg border border-[var(--n-border)] w-fit">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm transition-all ${
              tab === t.id
                ? "bg-[var(--hig-accent)]/10 text-[var(--hig-accent)] border border-[var(--hig-accent)]/20"
                : "text-muted-foreground hover:text-foreground"
            }`}
            data-testid={`admin-tab-${t.id}`}
          >
            <t.icon className="w-4 h-4" />
            {t.label}
          </button>
        ))}
      </div>

      {/* Users Tab */}
      {tab === "users" && (
        <div className="space-y-4">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search users..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 bg-[var(--n-hover)] border-[var(--n-border)]"
              data-testid="admin-user-search"
            />
          </div>

          <div className="space-y-2" data-testid="admin-user-list">
            {filteredUsers.map((u) => {
              const sub = u.subscription || {};
              const planColor = PLAN_COLORS[sub.plan_id] || "cyan";
              const isTrial = sub.trial_end && new Date(sub.trial_end) > new Date();

              return (
                <div
                  key={u.id}
                  className="glass-card rounded-xl p-4 flex items-center justify-between flex-wrap gap-3"
                  data-testid={`admin-user-${u.id}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-full bg-[var(--hig-accent)]/20 flex items-center justify-center text-[var(--hig-accent)] text-xs font-bold shrink-0">
                      {u.name?.[0]?.toUpperCase() || "U"}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium truncate">{u.name}</p>
                        {u.role === "admin" && (
                          <Badge className="bg-red-500/20 text-red-600 dark:text-red-400 text-[10px]">Admin</Badge>
                        )}
                        {u.role === "operator" && (
                          <Badge className="bg-violet-500/20 text-violet-600 dark:text-violet-400 text-[10px]">Operator</Badge>
                        )}
                        {u.role === "viewer" && (
                          <Badge className="bg-zinc-500/20 text-[var(--n-text-muted)] text-[10px]">Viewer</Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={`bg-${planColor}-500/20 text-${planColor}-400`}>
                      {sub.plan_id?.toUpperCase() || "FREE"}
                    </Badge>
                    {u.locked && (
                      <Badge className="bg-red-500/20 text-red-600 dark:text-red-400 text-[10px]">
                        <Lock className="w-2.5 h-2.5 mr-0.5" />Locked
                      </Badge>
                    )}
                    {sub.bypass && (
                      <Badge variant="outline" className="text-violet-600 dark:text-violet-400 border-violet-500/30 text-[10px]">
                        Bypass
                      </Badge>
                    )}
                    {isTrial && (
                      <Badge variant="outline" className="text-emerald-600 dark:text-emerald-400 border-emerald-500/30 text-[10px]">
                        Trial
                      </Badge>
                    )}
                    {u.locked && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 text-xs text-amber-600 dark:text-amber-400"
                        onClick={() => handleUnlockUser(u.id)}
                        data-testid={`admin-unlock-${u.id}`}
                      >
                        <Unlock className="w-3 h-3 mr-1" />
                        Unlock
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDrawer(u)}
                      data-testid={`admin-edit-user-${u.id}`}
                    >
                      <Edit className="w-3.5 h-3.5 mr-1" />
                      Manage
                    </Button>
                  </div>
                </div>
              );
            })}
            {filteredUsers.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-8">No users found</p>
            )}
          </div>
        </div>
      )}

      {/* Plans Tab */}
      {tab === "plans" && (
        <div className="space-y-4" data-testid="admin-plans-list">
          {plans.map((plan) => {
            const color = PLAN_COLORS[plan.plan_id] || "cyan";
            return (
              <div key={plan.plan_id} className="glass-card rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg bg-${color}-500/20 flex items-center justify-center`}>
                      {(() => { const I = PLAN_ICONS[plan.plan_id] || Zap; return <I className={`w-5 h-5 text-${color}-400`} />; })()}
                    </div>
                    <div>
                      <h3 className="font-semibold">{plan.name}</h3>
                      <p className="text-sm text-muted-foreground">${plan.price_monthly}/mo</p>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {Object.entries(plan.features || {}).map(([key, val]) => (
                    <div key={key} className="flex items-center justify-between p-2 rounded-lg bg-[var(--n-hover)] border border-[var(--n-border)]">
                      <span className="text-xs text-[var(--n-text-muted)] capitalize">
                        {key.replace(/_/g, " ")}
                      </span>
                      {typeof val === "boolean" ? (
                        <Switch
                          checked={val}
                          onCheckedChange={(checked) => {
                            const updatedFeatures = { ...plan.features, [key]: checked };
                            handleUpdatePlan(plan.plan_id, { features: updatedFeatures });
                          }}
                          className="scale-75"
                        />
                      ) : (
                        <span className="text-xs font-mono text-[var(--hig-accent)]">
                          {val === -1 ? "∞" : val}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Feature Gates Tab */}
      {tab === "gates" && (
        <div className="space-y-3" data-testid="admin-gates-list">
          {gates.map((gate) => (
            <div key={gate.feature_key} className="glass-card rounded-xl p-4 flex items-center justify-between flex-wrap gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-[var(--hig-accent)]" />
                  <p className="text-sm font-medium">{gate.display_name}</p>
                </div>
                <p className="text-xs text-muted-foreground ml-6">{gate.description}</p>
              </div>
              <div className="flex items-center gap-3">
                <Select
                  value={gate.required_plan}
                  onValueChange={(val) => handleUpdateGate(gate.feature_key, { required_plan: val })}
                >
                  <SelectTrigger className="w-28 h-8 text-xs bg-[var(--n-hover)] border-[var(--n-border)]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="pro">Pro</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
                <Switch
                  checked={gate.enabled}
                  onCheckedChange={(checked) => handleUpdateGate(gate.feature_key, { enabled: checked })}
                  data-testid={`gate-toggle-${gate.feature_key}`}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Analytics Tab */}
      {tab === "analytics" && <AnalyticsTab api={api} />}

      {/* Data Management Tab */}
      {tab === "data" && <DataManagementTab api={api} />}

      {/* SSO Tab */}
      {tab === "sso" && <SSOTab api={api} />}

      {/* Service Health Tab */}
      {tab === "health" && <ServiceHealthTab api={api} />}

      {/* User Edit Drawer */}
      <Sheet open={!!drawer} onOpenChange={(open) => !open && setDrawer(null)}>
        <SheetContent className="w-full sm:w-[420px] bg-[var(--n-surface)] border-[var(--border-color)] overflow-y-auto">
          <SheetTitle className="text-lg font-semibold mb-6">
            Manage User
          </SheetTitle>
          {drawer && <UserDrawer user={drawer} plans={plans} gates={gates} onSave={loadData} onClose={() => setDrawer(null)} saving={saving} api={api} onSetPlan={handleSetPlan} onSetBypass={handleSetBypass} onStartTrial={handleStartTrial} onSetRole={handleSetRole} onFeatureOverride={handleFeatureOverride} onUnlock={handleUnlockUser} onResetPassword={handleResetPassword} />}
        </SheetContent>
      </Sheet>
    </div>
  );
}


// ============================================================================
// Data Management Tab - Admin data cleanup for Data Plane
// ============================================================================

function DataManagementTab({ api }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purging, setPurging] = useState(false);
  const [confirmPhrase, setConfirmPhrase] = useState("");
  const [selectedEnv, setSelectedEnv] = useState("");
  const [integrityResult, setIntegrityResult] = useState(null);
  const [checkingIntegrity, setCheckingIntegrity] = useState(false);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api().get("/admin/data/stats");
      setStats(res.data);
    } catch (err) {
      toast.error("Failed to load data stats");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handlePurge = async (purgeType) => {
    if (purgeType === "all" && confirmPhrase !== "PURGE ALL DATA") {
      toast.error("Type 'PURGE ALL DATA' to confirm full purge");
      return;
    }
    if (purgeType === "environment" && !selectedEnv) {
      toast.error("Select an environment to purge");
      return;
    }

    setPurging(true);
    try {
      const payload = {
        purge_type: purgeType,
        environment_id: purgeType === "environment" ? selectedEnv : undefined,
        confirm_phrase: purgeType === "all" ? confirmPhrase : undefined,
      };
      const res = await api().post("/admin/data/purge", payload);
      toast.success(res.data.message || `Purged ${res.data.purged} documents`);
      setConfirmPhrase("");
      setSelectedEnv("");
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Purge failed");
    } finally {
      setPurging(false);
    }
  };

  const handleIntegrityCheck = async () => {
    setCheckingIntegrity(true);
    try {
      const res = await api().get("/admin/data/integrity");
      setIntegrityResult(res.data);
      if (res.data.total === 0) {
        toast.success("Data integrity check passed - no issues found");
      } else {
        toast.warning(`Found ${res.data.total} integrity issue(s)`);
      }
    } catch (err) {
      toast.error("Integrity check failed");
    } finally {
      setCheckingIntegrity(false);
    }
  };

  const handleFixOrphans = async () => {
    try {
      const res = await api().post("/admin/data/fix-orphans");
      toast.success(`Fixed: ${res.data.fixed.deployments} deployments, ${res.data.fixed.environments} environments, ${res.data.fixed.credentials} credentials`);
      fetchStats();
      handleIntegrityCheck();
    } catch (err) {
      toast.error("Fix failed");
    }
  };

  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[var(--hig-accent)]" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="data-management-tab">
      {/* Data Stats Overview */}
      <div className="glass-card rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Database className="w-4 h-4 text-[var(--hig-accent)]" />
            <span className="text-sm font-semibold">Data Plane Statistics</span>
          </div>
          <Button variant="ghost" size="sm" onClick={fetchStats} className="text-xs" data-testid="refresh-data-stats">
            <RefreshCw className="w-3 h-3 mr-1" /> Refresh
          </Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="p-3 rounded-lg bg-[var(--n-bg)] border border-[var(--n-border)]">
            <p className="text-[10px] text-[var(--n-text-muted)] uppercase">Total Documents</p>
            <p className="text-xl font-bold font-mono">{stats?.total_documents || 0}</p>
          </div>
          <div className="p-3 rounded-lg bg-[var(--n-bg)] border border-[var(--n-border)]">
            <p className="text-[10px] text-[var(--n-text-muted)] uppercase">Environments</p>
            <p className="text-xl font-bold font-mono">{stats?.collections?.environments || 0}</p>
          </div>
          <div className="p-3 rounded-lg bg-[var(--n-bg)] border border-[var(--n-border)]">
            <p className="text-[10px] text-[var(--n-text-muted)] uppercase">Credentials</p>
            <p className="text-xl font-bold font-mono">{stats?.collections?.azure_credentials || 0}</p>
          </div>
          <div className="p-3 rounded-lg bg-[var(--n-bg)] border border-[var(--n-border)]">
            <p className="text-[10px] text-[var(--n-text-muted)] uppercase">Resources</p>
            <p className="text-xl font-bold font-mono">{stats?.collections?.resources || 0}</p>
          </div>
        </div>

        {/* Collection breakdown */}
        {stats?.collections && Object.keys(stats.collections).length > 0 && (
          <div className="mt-4">
            <p className="text-xs text-[var(--n-text-muted)] mb-2">Collection Breakdown:</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(stats.collections)
                .filter(([, count]) => count > 0)
                .sort((a, b) => b[1] - a[1])
                .map(([name, count]) => (
                  <div key={name} className="px-2 py-1 rounded-lg bg-[var(--n-hover)] border border-[var(--n-border)] text-[10px]">
                    <span className="text-[var(--n-text-muted)]">{name}:</span> <span className="font-mono font-bold">{count}</span>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>

      {/* Environment Data */}
      {stats?.environments?.length > 0 && (
        <div className="glass-card rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <Layers className="w-4 h-4 text-violet-500" />
            <span className="text-sm font-semibold">Data by Environment</span>
          </div>
          <div className="space-y-2">
            {stats.environments.map((env) => (
              <div key={env.id} className="flex items-center justify-between p-3 rounded-lg bg-[var(--n-bg)] border border-[var(--n-border)]">
                <div>
                  <span className="text-sm font-medium">{env.name}</span>
                  <div className="flex items-center gap-3 text-[10px] text-[var(--n-text-muted)] mt-0.5">
                    <span>{env.credentials} credentials</span>
                    <span>{env.subscriptions} subscriptions</span>
                    <span>{env.data_documents} documents</span>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-[10px] h-7 text-red-500 border-red-300 hover:bg-red-500/10"
                  onClick={() => { setSelectedEnv(env.id); handlePurge("environment"); }}
                  disabled={purging}
                  data-testid={`purge-env-${env.id}`}
                >
                  <Trash2 className="w-3 h-3 mr-1" /> Purge
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Data Integrity */}
      <div className="glass-card rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-emerald-500" />
            <span className="text-sm font-semibold">Data Integrity</span>
          </div>
          <div className="flex items-center gap-2">
            {integrityResult && integrityResult.total > 0 && (
              <Button variant="outline" size="sm" onClick={handleFixOrphans} className="text-[10px] h-7" data-testid="fix-orphans-btn">
                Fix Orphans
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={handleIntegrityCheck} disabled={checkingIntegrity} className="text-[10px] h-7" data-testid="check-integrity-btn">
              {checkingIntegrity ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />}
              Check Integrity
            </Button>
          </div>
        </div>

        {integrityResult && (
          <div className="space-y-2">
            <div className="flex items-center gap-4 text-xs text-[var(--n-text-muted)]">
              <span>{integrityResult.deployments} deployments</span>
              <span>{integrityResult.environments} environments</span>
              <span>{integrityResult.credentials} credentials</span>
            </div>
            {integrityResult.issues?.length > 0 ? (
              <div className="space-y-1.5 mt-3">
                {integrityResult.issues.map((issue, i) => (
                  <div key={i} className="p-2.5 rounded-lg bg-red-500/5 border border-red-500/20 text-[11px]">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-3.5 h-3.5 text-red-500 shrink-0 mt-0.5" />
                      <div>
                        <span className="font-medium text-red-600 dark:text-red-400">{issue.type}</span>
                        <span className="text-[var(--n-text-muted)]"> — {issue.entity}</span>
                        <p className="text-[var(--n-text-secondary)] mt-0.5">{issue.detail}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20 flex items-center gap-2 text-xs text-emerald-600 dark:text-emerald-400">
                <Check className="w-4 h-4" /> All relationships are consistent. No orphaned data.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Danger Zone */}
      <div className="glass-card rounded-xl p-5 border-red-500/30">
        <div className="flex items-center gap-2 mb-4">
          <AlertTriangle className="w-4 h-4 text-red-500" />
          <span className="text-sm font-semibold text-red-600 dark:text-red-400">Danger Zone</span>
        </div>

        <div className="space-y-4">
          {/* Purge Orphans */}
          <div className="flex items-center justify-between p-3 rounded-lg bg-[var(--n-bg)] border border-[var(--n-border)]">
            <div>
              <p className="text-sm font-medium">Purge Orphaned Data</p>
              <p className="text-[10px] text-[var(--n-text-muted)]">Remove data not linked to any credential. Safe operation.</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePurge("orphans")}
              disabled={purging}
              className="text-[10px] h-7 text-amber-600 border-amber-300 hover:bg-amber-500/10"
              data-testid="purge-orphans-btn"
            >
              {purging ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Trash2 className="w-3 h-3 mr-1" />}
              Purge Orphans
            </Button>
          </div>

          {/* Full Purge */}
          <div className="p-4 rounded-lg bg-red-500/5 border border-red-500/20">
            <p className="text-sm font-medium text-red-600 dark:text-red-400 mb-2">Full Data Purge</p>
            <p className="text-[10px] text-[var(--n-text-muted)] mb-3">
              This will delete ALL scanned data from the Data Plane (resources, changes, costs, etc.). Credentials and environments will remain. This action cannot be undone.
            </p>
            <div className="flex items-center gap-3">
              <Input
                type="text"
                placeholder="Type 'PURGE ALL DATA' to confirm"
                value={confirmPhrase}
                onChange={(e) => setConfirmPhrase(e.target.value)}
                className="flex-1 text-sm bg-[var(--n-hover)] border-red-500/30"
                data-testid="purge-confirm-input"
              />
              <Button
                variant="destructive"
                size="sm"
                onClick={() => handlePurge("all")}
                disabled={purging || confirmPhrase !== "PURGE ALL DATA"}
                className="text-[10px] h-8"
                data-testid="purge-all-btn"
              >
                {purging ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Trash2 className="w-3 h-3 mr-1" />}
                Purge All Data
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


const PRIORITY_COLORS = { P0: "text-red-600 dark:text-red-400 bg-red-500/10 border-red-500/20", P1: "text-amber-600 dark:text-amber-400 bg-amber-500/10 border-amber-500/20", P2: "text-[var(--hig-accent)] bg-[var(--hig-accent)]/10 border-[var(--hig-accent)]/20" };
const CATEGORY_COLORS = { feature: "text-violet-600 dark:text-violet-400", ux: "text-[var(--hig-accent)]", performance: "text-amber-600 dark:text-amber-400", retention: "text-emerald-600 dark:text-emerald-400" };
const IMPACT_COLORS = { high: "text-red-600 dark:text-red-400", medium: "text-amber-600 dark:text-amber-400", low: "text-[var(--n-text-muted)]" };

function AnalyticsTab({ api }) {
  const [summary, setSummary] = useState(null);
  const [prompts, setPrompts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(null);
  const [days, setDays] = useState(30);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryRes, promptsRes] = await Promise.all([
        api().get(`/analytics/summary?days=${days}`),
        api().get("/analytics/prompts?limit=5"),
      ]);
      setSummary(summaryRes.data);
      setPrompts(promptsRes.data);
    } catch {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, [api, days]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const generatePrompts = async () => {
    setGenerating(true);
    try {
      const res = await api().post("/analytics/generate-prompts");
      if (res.data.error) { toast.error(res.data.error); return; }
      toast.success(`Generated ${res.data.prompts?.length || 0} development prompts`);
      fetchData();
    } catch {
      toast.error("Failed to generate prompts");
    } finally {
      setGenerating(false);
    }
  };

  const copyPrompt = (prompt) => {
    const text = `[${prompt.priority}] ${prompt.title}\n\n${prompt.description}\n\nEvidence: ${prompt.evidence}\nCategory: ${prompt.category} | Impact: ${prompt.estimated_impact}`;
    navigator.clipboard.writeText(text);
    setCopied(prompt.id);
    toast.success("Prompt copied!");
    setTimeout(() => setCopied(null), 2000);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-40"><Loader2 className="w-5 h-5 animate-spin text-[var(--hig-accent)]" /></div>;
  }

  const latestGeneration = prompts?.prompts?.[0];

  return (
    <div className="space-y-6" data-testid="analytics-tab">
      {/* Stats Overview */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger className="w-24 h-8 text-xs bg-[var(--n-hover)] border-[var(--n-border)]" data-testid="analytics-days-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 days</SelectItem>
              <SelectItem value="14">14 days</SelectItem>
              <SelectItem value="30">30 days</SelectItem>
              <SelectItem value="90">90 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button variant="ghost" size="icon" onClick={fetchData} className="h-8 w-8 text-[var(--n-text-dim)] hover:text-[var(--hig-accent)]" data-testid="analytics-refresh">
          <RefreshCw className="w-3.5 h-3.5" />
        </Button>
      </div>

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "Total Events", value: summary.total_events, icon: BarChart3, color: "cyan" },
            { label: "Unique Users", value: summary.unique_users, icon: Users, color: "violet" },
            { label: "Pages Tracked", value: Object.keys(summary.page_views || {}).length, icon: Eye, color: "amber" },
            { label: "Features Used", value: Object.keys(summary.feature_usage || {}).length, icon: MousePointer, color: "emerald" },
          ].map((s) => (
            <div key={s.label} className="glass-card rounded-xl p-4" data-testid={`analytics-stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}>
              <div className="flex items-center gap-2 mb-2">
                <s.icon className={`w-4 h-4 text-${s.color}-400`} />
                <span className="text-[10px] text-[var(--n-text-muted)] uppercase">{s.label}</span>
              </div>
              <p className="text-xl font-bold font-mono">{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Page Views */}
      {summary?.page_views && Object.keys(summary.page_views).length > 0 && (
        <div className="glass-card rounded-xl p-5 space-y-3" data-testid="page-views-section">
          <div className="flex items-center gap-2 mb-1">
            <Eye className="w-4 h-4 text-[var(--hig-accent)]" />
            <span className="text-sm font-semibold">Page Views</span>
          </div>
          <div className="space-y-2">
            {Object.entries(summary.page_views).sort((a, b) => b[1].views - a[1].views).map(([page, data]) => {
              const maxViews = Math.max(...Object.values(summary.page_views).map((d) => d.views));
              const pct = maxViews > 0 ? (data.views / maxViews) * 100 : 0;
              return (
                <div key={page} className="flex items-center gap-3">
                  <span className="text-[11px] text-[var(--n-text-secondary)] w-28 truncate shrink-0">{page}</span>
                  <div className="flex-1 h-5 bg-[var(--n-hover)] rounded-full overflow-hidden relative">
                    <div className="h-full rounded-full bg-gradient-to-r from-[var(--hig-accent)]/30 to-[var(--hig-accent)]/10 transition-all" style={{ width: `${pct}%` }} />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-mono text-[var(--n-text-muted)]">{data.views}</span>
                  </div>
                  <span className="text-[10px] text-[var(--n-text-dim)] w-16 shrink-0 text-right" title="Avg dwell time">
                    {data.avg_dwell_ms > 0 ? `${(data.avg_dwell_ms / 1000).toFixed(1)}s` : "--"}
                  </span>
                </div>
              );
            })}
          </div>
          <p className="text-[10px] text-[var(--n-text-dim)]">Avg dwell time shown on the right. Low dwell = users bouncing.</p>
        </div>
      )}

      {/* Feature Usage */}
      {summary?.feature_usage && Object.keys(summary.feature_usage).length > 0 && (
        <div className="glass-card rounded-xl p-5 space-y-3" data-testid="feature-usage-section">
          <div className="flex items-center gap-2 mb-1">
            <MousePointer className="w-4 h-4 text-violet-600 dark:text-violet-400" />
            <span className="text-sm font-semibold">Feature Usage</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(summary.feature_usage).sort((a, b) => b[1] - a[1]).map(([feature, count]) => (
              <div key={feature} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[var(--n-hover)] border border-[var(--n-border)]">
                <span className="text-[11px] text-[var(--n-text-secondary)]">{feature}</span>
                <span className="text-[10px] font-mono font-bold text-violet-600 dark:text-violet-400">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top Users */}
      {summary?.top_users?.length > 0 && (
        <div className="glass-card rounded-xl p-5 space-y-3" data-testid="top-users-section">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
            <span className="text-sm font-semibold">Top Users</span>
          </div>
          <div className="space-y-1.5">
            {summary.top_users.slice(0, 5).map((u, i) => (
              <div key={u.email} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-[var(--n-hover)]">
                <span className="text-[10px] text-[var(--n-text-dim)] w-4 font-mono">{i + 1}</span>
                <span className="text-[11px] text-[var(--n-text-secondary)] flex-1 truncate">{u.email}</span>
                <span className="text-[10px] font-mono text-foreground">{u.events} events</span>
                <span className="text-[10px] text-[var(--n-text-dim)]">{u.pages_visited} pages</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AI Prompt Generation */}
      <div className="glass-card rounded-xl p-5 space-y-4" data-testid="prompt-generation-section">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Brain className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            <span className="text-sm font-semibold">AI Development Prompts</span>
          </div>
          <Button
            size="sm"
            onClick={generatePrompts}
            disabled={generating || (summary?.total_events || 0) === 0}
            className="text-[10px] h-7 bg-amber-500 hover:bg-amber-600 text-primary-foreground font-semibold"
            data-testid="generate-prompts-btn"
          >
            {generating ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Brain className="w-3 h-3 mr-1" />}
            {generating ? "Analyzing..." : "Generate Prompts"}
          </Button>
        </div>
        <p className="text-[10px] text-[var(--n-text-muted)]">
          Analyzes {days}-day user behavior to generate prioritized development tasks. Requires analytics events.
        </p>

        {latestGeneration && (
          <div className="space-y-3">
            <div className="px-3 py-2 rounded-lg bg-[var(--hig-accent)]/[0.03] border border-[var(--hig-accent)]/15 text-[11px] text-[var(--n-text-secondary)]" data-testid="prompt-analysis">
              <span className="text-[var(--hig-accent)] font-semibold text-[10px] uppercase tracking-wider block mb-1">Analysis</span>
              {latestGeneration.analysis}
            </div>
            <div className="flex items-center gap-2 text-[10px] text-[var(--n-text-dim)]">
              <Clock className="w-3 h-3" />
              Generated {new Date(latestGeneration.generated_at).toLocaleString()} by {latestGeneration.generated_by}
              <span className="ml-auto">{latestGeneration.total_events_analyzed || 0} events analyzed</span>
            </div>

            <div className="space-y-2">
              {(latestGeneration.prompts || []).map((p) => (
                <div key={p.id} className={`rounded-xl border p-4 space-y-2 ${PRIORITY_COLORS[p.priority] || "border-[var(--n-border)]"}`} data-testid={`prompt-card-${p.id}`}>
                  <div className="flex items-start gap-2">
                    <Badge className={`text-[10px] shrink-0 ${PRIORITY_COLORS[p.priority] || ""}`}>{p.priority}</Badge>
                    <span className="text-[12px] font-semibold flex-1">{p.title}</span>
                    <button onClick={() => copyPrompt(p)} className="text-[var(--n-text-dim)] hover:text-foreground shrink-0" data-testid={`copy-prompt-${p.id}`}>
                      {copied === p.id ? <Check className="w-3 h-3 text-emerald-600 dark:text-emerald-400" /> : <Copy className="w-3 h-3" />}
                    </button>
                  </div>
                  <p className="text-[11px] text-[var(--n-text-secondary)] leading-relaxed">{p.description}</p>
                  <div className="flex items-center gap-3 text-[10px]">
                    <span className={CATEGORY_COLORS[p.category] || "text-[var(--n-text-muted)]"}>{p.category}</span>
                    <span className={IMPACT_COLORS[p.estimated_impact] || "text-[var(--n-text-muted)]"}>{p.estimated_impact} impact</span>
                    {p.evidence && <span className="text-[var(--n-text-dim)] truncate flex-1" title={p.evidence}>{p.evidence}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {!latestGeneration && (
          <div className="text-center py-8">
            <BarChart3 className="w-8 h-8 text-[var(--n-border-3)] mx-auto mb-2" />
            <p className="text-[11px] text-[var(--n-text-muted)]">No prompts generated yet. Click "Generate Prompts" to analyze user behavior and create development tasks.</p>
          </div>
        )}
      </div>
    </div>
  );
}


function UserDrawer({ user: u, plans, gates, onSetPlan, onSetBypass, onStartTrial, onSetRole, onFeatureOverride, onUnlock, onResetPassword, saving }) {
  const sub = u.subscription || {};
  const [bypassReason, setBypassReason] = useState(sub.bypass_reason || "");
  const [trialDays, setTrialDays] = useState(14);
  const [overrides, setOverrides] = useState(sub.feature_overrides || {});
  const [newPassword, setNewPassword] = useState("");
  const [showResetForm, setShowResetForm] = useState(false);

  return (
    <div className="space-y-6" data-testid="user-drawer">
      {/* User Info */}
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-full bg-[var(--hig-accent)]/20 flex items-center justify-center text-[var(--hig-accent)] font-bold">
          {u.name?.[0]?.toUpperCase() || "U"}
        </div>
        <div>
          <p className="font-medium">{u.name}</p>
          <p className="text-sm text-muted-foreground">{u.email}</p>
        </div>
      </div>

      {/* Role */}
      <div>
        <label className="text-xs text-muted-foreground mb-2 block">Role</label>
        <Select value={u.role || "user"} onValueChange={(val) => onSetRole(u.id, val)}>
          <SelectTrigger className="bg-[var(--n-hover)] border-[var(--n-border)]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="admin">Admin - Full access</SelectItem>
            <SelectItem value="operator">Operator - Manage credentials, scans, settings</SelectItem>
            <SelectItem value="user">User - Standard access</SelectItem>
            <SelectItem value="viewer">Viewer - Read-only</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Account Security */}
      <div className="glass-card rounded-lg p-4 space-y-3">
        <p className="text-sm font-medium flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-[var(--hig-accent)]" />
          Account Security
        </p>
        {u.locked && (
          <div className="flex items-center justify-between p-2 rounded-lg bg-red-500/10 border border-red-500/20">
            <div className="flex items-center gap-2">
              <Lock className="w-3.5 h-3.5 text-red-600 dark:text-red-400" />
              <span className="text-xs text-red-600 dark:text-red-400">Account locked ({u.failed_attempts} failed attempts)</span>
            </div>
            <Button size="sm" variant="outline" className="h-6 text-xs" onClick={() => onUnlock(u.id)} data-testid="drawer-unlock-btn">
              <Unlock className="w-3 h-3 mr-1" />Unlock
            </Button>
          </div>
        )}
        {showResetForm ? (
          <div className="space-y-2">
            <Input
              type="password"
              placeholder="New password (min 8 chars)"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="bg-[var(--n-hover)] border-[var(--n-border)] text-sm"
              data-testid="admin-new-password-input"
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                className="text-xs"
                disabled={newPassword.length < 8 || saving}
                onClick={() => { onResetPassword(u.id, newPassword); setNewPassword(""); setShowResetForm(false); }}
                data-testid="admin-confirm-reset-btn"
              >
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : "Confirm Reset"}
              </Button>
              <Button size="sm" variant="ghost" className="text-xs" onClick={() => { setShowResetForm(false); setNewPassword(""); }}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <Button
            size="sm"
            variant="outline"
            className="text-xs w-full"
            onClick={() => setShowResetForm(true)}
            data-testid="admin-reset-password-btn"
          >
            <KeyRound className="w-3 h-3 mr-1" />
            Reset Password
          </Button>
        )}
      </div>

      {/* Plan */}
      <div>
        <label className="text-xs text-muted-foreground mb-2 block">Plan</label>
        <Select value={sub.plan_id || "free"} onValueChange={(val) => onSetPlan(u.id, val)}>
          <SelectTrigger className="bg-[var(--n-hover)] border-[var(--n-border)]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {plans.map((p) => (
              <SelectItem key={p.plan_id} value={p.plan_id}>
                {p.name} (${p.price_monthly}/mo)
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Bypass */}
      <div className="glass-card rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium">Payment Bypass</p>
            <p className="text-xs text-muted-foreground">Skip payment for this user</p>
          </div>
          <Switch
            checked={sub.bypass || false}
            onCheckedChange={(checked) => onSetBypass(u.id, checked, bypassReason)}
            data-testid="bypass-toggle"
          />
        </div>
        {sub.bypass && (
          <Input
            placeholder="Reason (partner, pilot, internal...)"
            value={bypassReason}
            onChange={(e) => setBypassReason(e.target.value)}
            onBlur={() => onSetBypass(u.id, true, bypassReason)}
            className="bg-[var(--n-hover)] border-[var(--n-border)] text-sm"
          />
        )}
      </div>

      {/* Trial */}
      <div className="glass-card rounded-lg p-4 space-y-3">
        <p className="text-sm font-medium flex items-center gap-2">
          <Clock className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
          Trial Access
        </p>
        {sub.trial_end && new Date(sub.trial_end) > new Date() ? (
          <p className="text-xs text-emerald-600 dark:text-emerald-400">
            Active until {new Date(sub.trial_end).toLocaleDateString()}
          </p>
        ) : (
          <p className="text-xs text-muted-foreground">No active trial</p>
        )}
        <div className="flex items-center gap-2">
          <Input
            type="number"
            min="1"
            max="365"
            value={trialDays}
            onChange={(e) => setTrialDays(parseInt(e.target.value) || 14)}
            className="w-20 bg-[var(--n-hover)] border-[var(--n-border)] text-sm"
          />
          <span className="text-xs text-muted-foreground">days</span>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onStartTrial(u.id, trialDays)}
            disabled={saving}
            data-testid="start-trial-btn"
          >
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : "Start Trial"}
          </Button>
        </div>
      </div>

      {/* Feature Overrides */}
      <div className="glass-card rounded-lg p-4 space-y-3">
        <p className="text-sm font-medium flex items-center gap-2">
          <Settings className="w-4 h-4 text-violet-600 dark:text-violet-400" />
          Feature Overrides
        </p>
        <p className="text-xs text-muted-foreground">
          Enable specific features regardless of plan
        </p>
        <div className="space-y-2">
          {gates.map((gate) => (
            <div key={gate.feature_key} className="flex items-center justify-between py-1">
              <span className="text-xs text-[var(--n-text-muted)]">{gate.display_name}</span>
              <Switch
                checked={overrides[gate.feature_key] || false}
                onCheckedChange={(checked) => {
                  const next = { ...overrides, [gate.feature_key]: checked };
                  setOverrides(next);
                  onFeatureOverride(u.id, next);
                }}
                className="scale-75"
                data-testid={`override-${gate.feature_key}`}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


function SSOTab({ api }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ client_id: "", client_secret: "", tenant_id: "", domain: "" });

  useEffect(() => {
    api().get("/sso/config").then(r => {
      setConfig(r.data);
      if (r.data) setForm({ client_id: r.data.client_id || "", client_secret: "", tenant_id: r.data.tenant_id || "", domain: r.data.domain || "" });
    }).catch(() => {}).finally(() => setLoading(false));
  }, [api]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api().post("/sso/config", { provider: "entra_id", ...form });
      setConfig(res.data);
      toast.success("SSO configuration saved");
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const handleToggle = async () => {
    try {
      const res = await api().put("/sso/toggle");
      setConfig(prev => ({ ...prev, enabled: res.data.enabled }));
      toast.success(res.data.enabled ? "SSO enabled" : "SSO disabled");
    } catch (err) { toast.error(err.response?.data?.detail || "Toggle failed"); }
  };

  if (loading) return <div className="flex justify-center py-8"><Settings className="w-5 h-5 animate-spin text-[var(--hig-accent)]" /></div>;

  return (
    <div className="space-y-4" data-testid="sso-tab">
      <div className="glass-card rounded-xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-[var(--hig-accent)]" />
            <span className="text-sm font-medium">Microsoft Entra ID SSO</span>
            {config?.enabled && <Badge className="text-[8px] px-1.5 py-0 bg-emerald-500/10 text-emerald-500">Enabled</Badge>}
          </div>
          {config?.client_id && !config.client_id.startsWith("PLACEHOLDER") && (
            <Switch checked={config?.enabled || false} onCheckedChange={handleToggle} data-testid="sso-toggle" />
          )}
        </div>
        <p className="text-xs text-[var(--n-text-muted)]">
          Allow users to sign in with their Microsoft work account. Create a separate "Niyant SSO" App Registration in Entra ID (single-tenant, openid profile email only).
        </p>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <label className="text-xs font-medium">Client ID (Application ID)</label>
            <Input value={form.client_id} onChange={e => setForm(p => ({ ...p, client_id: e.target.value }))} placeholder="From App Registration" className="text-sm" data-testid="sso-client-id" />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium">Client Secret</label>
            <Input type="password" value={form.client_secret} onChange={e => setForm(p => ({ ...p, client_secret: e.target.value }))} placeholder={config?.client_secret === "********" ? "Saved (enter to change)" : "From App Registration"} className="text-sm" data-testid="sso-client-secret" />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium">Tenant ID</label>
            <Input value={form.tenant_id} onChange={e => setForm(p => ({ ...p, tenant_id: e.target.value }))} placeholder="Your Azure tenant ID or 'common'" className="text-sm" data-testid="sso-tenant-id" />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium">Allowed Domain <span className="text-[var(--n-text-muted)] font-normal">(optional)</span></label>
            <Input value={form.domain} onChange={e => setForm(p => ({ ...p, domain: e.target.value }))} placeholder="e.g. yourcompany.com" className="text-sm" data-testid="sso-domain" />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={handleSave} disabled={saving || !form.client_id} className="bg-[var(--hig-accent)] text-white hover:bg-[var(--hig-accent)]/90 text-sm" data-testid="sso-save-btn">
            {saving ? <Settings className="w-4 h-4 animate-spin mr-1.5" /> : <Shield className="w-4 h-4 mr-1.5" />} Save SSO Configuration
          </Button>
          {config?.redirect_uri && (
            <span className="text-[10px] text-[var(--n-text-muted)] font-mono">Redirect: {config.redirect_uri}</span>
          )}
        </div>
      </div>
    </div>
  );
}

function ServiceHealthTab({ api }) {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkHealth();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkHealth = async () => {
    setLoading(true);
    try {
      const [emailRes, ssoRes, oauthRes, depRes] = await Promise.all([
        api().get("/email/status").catch(() => ({ data: {} })),
        api().get("/sso/config").catch(() => ({ data: {} })),
        api().get("/azure-oauth/config").catch(() => ({ data: {} })),
        api().get("/deployments").catch(() => ({ data: { deployments: [] } })),
      ]);

      const dep = depRes.data.deployments?.[0];
      let endpointHealth = null;
      if (dep?.id && dep?.endpoint_url) {
        try {
          const h = await api().get(`/deployments/${dep.id}/health`);
          endpointHealth = h.data;
        } catch {}
      }

      setHealth({
        email: { live: emailRes.data.live || false, mode: emailRes.data.live ? "ACS" : "Mock" },
        sso: { configured: !!(ssoRes.data?.client_id && !ssoRes.data?.client_id?.startsWith("PLACEHOLDER")), enabled: ssoRes.data?.enabled || false },
        scanner: { configured: oauthRes.data?.configured || false },
        deployment: dep ? { name: dep.name, status: dep.status, endpoint: dep.endpoint_url } : null,
        endpoint: endpointHealth,
      });
    } catch {} finally { setLoading(false); }
  };

  if (loading) return <div className="flex justify-center py-8"><Settings className="w-5 h-5 animate-spin text-[var(--hig-accent)]" /></div>;

  const services = [
    { name: "App Service (Endpoint)", status: health?.endpoint?.healthy ? "healthy" : health?.deployment?.endpoint ? "unhealthy" : "not deployed", detail: health?.deployment?.endpoint || "No deployment", link: health?.deployment?.endpoint },
    { name: "Email (ACS)", status: health?.email?.live ? "connected" : "mock mode", detail: health?.email?.live ? "Azure Communication Services active" : "Emails stored in DB only — seed ACS connection string in Key Vault", link: null },
    { name: "SSO (Entra ID)", status: health?.sso?.enabled ? "enabled" : health?.sso?.configured ? "configured (disabled)" : "not configured", detail: health?.sso?.enabled ? "Users can sign in with Microsoft" : "Configure in SSO tab above", link: null },
    { name: "Customer Scanner (OAuth)", status: health?.scanner?.configured ? "configured" : "not configured", detail: health?.scanner?.configured ? "Multi-tenant app ready" : "Run 'Create Customer Auth' from Deployments → Modify", link: null },
    { name: "Database (Cosmos DB)", status: "connected", detail: "Connected via Key Vault secret (mongo-url)", link: null },
    { name: "AI (LLM)", status: "connected", detail: "LLM key via Key Vault", link: null },
  ];

  const statusIcon = (s) => {
    if (["healthy", "connected", "enabled", "configured"].includes(s)) return <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />;
    if (s.includes("mock") || s.includes("disabled") || s.includes("configured")) return <div className="w-2.5 h-2.5 rounded-full bg-amber-500" />;
    return <div className="w-2.5 h-2.5 rounded-full bg-red-500" />;
  };

  return (
    <div className="space-y-4" data-testid="health-tab">
      <div className="glass-card rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-[var(--hig-accent)]" />
            <span className="text-sm font-medium">Platform Service Health</span>
          </div>
          <Button variant="ghost" size="sm" onClick={checkHealth} className="text-xs" data-testid="refresh-health">
            Refresh
          </Button>
        </div>
        <div className="space-y-3">
          {services.map((svc, i) => (
            <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-[var(--n-bg)] border border-[var(--n-border)]" data-testid={`health-${svc.name.toLowerCase().replace(/[^a-z]/g, "-")}`}>
              <div className="flex items-center gap-3 min-w-0">
                {statusIcon(svc.status)}
                <div className="min-w-0">
                  <span className="text-xs font-medium block">{svc.name}</span>
                  <span className="text-[10px] text-[var(--n-text-muted)] block truncate">{svc.detail}</span>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Badge className={`text-[8px] px-1.5 py-0 ${
                  ["healthy", "connected", "enabled", "configured"].includes(svc.status) ? "bg-emerald-500/10 text-emerald-500" :
                  svc.status.includes("mock") || svc.status.includes("disabled") ? "bg-amber-500/10 text-amber-500" :
                  "bg-red-500/10 text-red-500"
                }`}>{svc.status}</Badge>
                {svc.link && (
                  <a href={svc.link} target="_blank" rel="noopener noreferrer" className="text-[var(--hig-accent)] text-[10px] hover:underline">Open</a>
                )}
              </div>
            </div>
          ))}
        </div>
        {health?.deployment && (
          <div className="mt-4 pt-3 border-t border-[var(--n-border)]">
            <p className="text-[10px] text-[var(--n-text-muted)]">
              Deployment: <span className="font-medium">{health.deployment.name}</span> ({health.deployment.status})
              {health.deployment.endpoint && <> — <a href={health.deployment.endpoint} target="_blank" rel="noopener noreferrer" className="text-[var(--hig-accent)] hover:underline">{health.deployment.endpoint}</a></>}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
